<template>
  <div class="kss-series-config-migration-tab p-6 max-w-6xl">
    <div class="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
      <h2 class="text-xl font-semibold text-blue-950 mb-2">
        <i class="pi pi-users mr-2"></i>
        KSS series config migration (Phase 1)
      </h2>
      <div class="text-sm text-blue-900/90 space-y-2 leading-relaxed">
        <p>
          Creates one <code class="bg-blue-100 px-1 rounded">kss_series</code> document per distinct
          KSS <code class="bg-blue-100 px-1 rounded">seriesId</code> on the
          <strong>active tenant</strong>. The config is the roster source of truth (OM-1185 / Round B)
          and the schedule source of truth (Phase 3 <code class="bg-blue-100 px-1 rounded">repeatRules</code>).
        </p>
        <p><strong>What is migrated:</strong></p>
        <ul class="list-disc list-inside ml-2 space-y-1">
          <li>Series name, description, creator, recurrence flags + <strong>repeatRules</strong> from assessment template</li>
          <li>
            Roster via Option B: users with
            <code class="bg-blue-100 px-1 rounded">scheduled|open|began</code>,
            else users on the latest occurrence date
          </li>
          <li>
            Historical-only users
            (<code class="bg-blue-100 px-1 rounded">submitted|expired|canceled</code> only)
            are excluded and listed for review
          </li>
          <li>
            Re-apply repairs wrong <code class="bg-blue-100 px-1 rounded">createdAt</code>
            and empty <code class="bg-blue-100 px-1 rounded">repeatRules</code>
          </li>
        </ul>
        <p class="font-medium">
          Always run dry run first. Apply only after reviewing the table and admin actions.
        </p>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 mb-6">
      <Button
        label="Run dry run"
        icon="pi pi-search"
        :loading="loadingDryRun"
        class="p-button-info"
        data-testid="kss-series-migrate-dry-run"
        @click="runDryRun"
      />
      <Button
        label="Apply migration"
        icon="pi pi-check"
        :loading="loadingApply"
        :disabled="!hasDryRunResults"
        class="p-button-success"
        data-testid="kss-series-migrate-apply"
        @click="runApply"
      />
      <Button
        v-if="hasDryRunResults || hasApplyResults"
        label="Clear results"
        icon="pi pi-times"
        class="p-button-secondary"
        :disabled="loadingDryRun || loadingApply"
        @click="clearResults"
      />
    </div>

    <div
      v-if="errorMessage"
      class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-800 text-sm"
    >
      {{ errorMessage }}
    </div>

    <div v-if="report" class="space-y-6" data-testid="kss-series-migrate-report">
      <div class="bg-slate-50 border border-slate-200 rounded-lg p-5">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 class="text-lg font-semibold text-slate-800">Summary</h3>
          <span
            class="rounded-full px-3 py-1 text-xs font-semibold"
            :class="report.dryRun ? 'bg-amber-100 text-amber-900' : 'bg-green-100 text-green-900'"
          >
            {{ report.dryRun ? 'Dry run (no writes)' : 'Applied' }}
          </span>
        </div>
        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 text-sm">
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">Tenant</div>
            <div class="font-mono text-xs break-all">{{ report.tenantSlug || '—' }}</div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">Scanned</div>
            <div class="text-2xl font-bold">{{ report.seriesScanned ?? 0 }}</div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">
              {{ report.dryRun ? 'Would create' : 'Created' }}
            </div>
            <div class="text-2xl font-bold text-blue-700">
              {{ wouldCreateOrCreatedCount }}
            </div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">Skipped existing</div>
            <div class="text-2xl font-bold">{{ report.seriesSkippedExisting ?? 0 }}</div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">Manual pick</div>
            <div
              class="text-2xl font-bold"
              :class="(report.seriesNeedingManualPick ?? 0) > 0 ? 'text-amber-700' : ''"
            >
              {{ report.seriesNeedingManualPick ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">Failed</div>
            <div
              class="text-2xl font-bold"
              :class="(report.seriesFailed ?? 0) > 0 ? 'text-red-700' : ''"
            >
              {{ report.seriesFailed ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">Missing after apply</div>
            <div
              class="text-2xl font-bold"
              :class="(report.seriesMissingAfterApply?.length ?? 0) > 0 ? 'text-red-700' : ''"
            >
              {{ report.seriesMissingAfterApply?.length ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">createdAt repaired</div>
            <div class="text-2xl font-bold">
              {{ createdAtRepairedCount }}
            </div>
          </div>
          <div class="bg-white rounded p-3 border border-slate-100">
            <div class="text-slate-500 text-xs mb-1">schedule repaired</div>
            <div class="text-2xl font-bold">
              {{ scheduleRepairedCount }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="report.adminActionRequired?.length"
        class="bg-amber-50 border border-amber-200 rounded-lg p-5"
      >
        <h3 class="text-lg font-semibold text-amber-950 mb-3">Admin action required</h3>
        <ul class="list-disc list-inside text-sm text-amber-950 space-y-1">
          <li v-for="(msg, i) in report.adminActionRequired" :key="i">
            {{ msg }}
          </li>
        </ul>
      </div>

      <div class="bg-white border border-slate-200 rounded-lg p-5">
        <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 class="text-lg font-semibold text-slate-800">Series details</h3>
          <span class="text-xs text-slate-500">
            {{ filteredRows.length }} of {{ report.rows?.length ?? 0 }} shown
          </span>
        </div>

        <div class="flex flex-wrap gap-2 mb-4">
          <Button
            v-for="opt in filterOptions"
            :key="opt.value"
            :label="opt.label"
            class="p-button-sm"
            :class="rowFilter === opt.value ? 'p-button-outlined' : 'p-button-text'"
            @click="rowFilter = opt.value"
          />
        </div>

        <div v-if="!filteredRows.length" class="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-600 text-sm">
          No series match this filter.
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full text-sm border border-slate-200">
            <thead class="bg-slate-50">
              <tr>
                <th class="text-left p-2 border-b">Series</th>
                <th class="text-left p-2 border-b">Series start</th>
                <th class="text-left p-2 border-b">Status</th>
                <th class="text-left p-2 border-b">Action</th>
                <th class="text-right p-2 border-b">Proposed roster</th>
                <th class="text-right p-2 border-b">Historical-only</th>
                <th class="text-left p-2 border-b">Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filteredRows"
                :key="row.seriesId"
                class="border-b border-slate-100 align-top"
                :class="row.emptyRosterNeedsManualPick ? 'bg-amber-50/40' : ''"
              >
                <td class="p-2">
                  <div class="font-semibold text-slate-900">{{ row.name || '—' }}</div>
                  <div class="font-mono text-[11px] text-slate-500 break-all mt-0.5">
                    {{ row.seriesId }}
                  </div>
                </td>
                <td class="p-2 text-xs text-slate-700 whitespace-nowrap">
                  {{ formatSeriesStart(row.seriesStartedAt) }}
                </td>
                <td class="p-2">
                  <span class="rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700">
                    {{ row.status }}
                  </span>
                </td>
                <td class="p-2">
                  <span
                    class="rounded-full px-2 py-0.5 text-xs font-semibold"
                    :class="actionBadgeClass(row.action)"
                  >
                    {{ row.action }}
                  </span>
                </td>
                <td class="p-2 text-right">
                  <div class="font-semibold">
                    {{ row.proposedParticipantUserIds?.length ?? 0 }}
                  </div>
                  <div
                    v-if="row.proposedParticipantUserIds?.length"
                    class="font-mono text-[10px] text-slate-500 mt-1 max-w-[14rem] ml-auto break-all"
                  >
                    {{ formatIdList(row.proposedParticipantUserIds) }}
                  </div>
                </td>
                <td class="p-2 text-right">
                  <div class="font-semibold">
                    {{ row.historicalOnlyUserIds?.length ?? 0 }}
                  </div>
                  <div
                    v-if="row.historicalOnlyUserIds?.length"
                    class="font-mono text-[10px] text-slate-500 mt-1 max-w-[14rem] ml-auto break-all"
                  >
                    {{ formatIdList(row.historicalOnlyUserIds) }}
                  </div>
                </td>
                <td class="p-2 text-xs text-slate-600">
                  <span v-if="row.emptyRosterNeedsManualPick" class="text-amber-800 font-medium">
                    Empty roster — pick participants in KSS tracker
                  </span>
                  <span v-else-if="row.action === 'failed'" class="text-red-800 font-medium">
                    {{ row.error || 'Backfill failed' }}
                  </span>
                  <span v-else-if="row.action === 'repaired_schedule'" class="text-indigo-800 font-medium">
                    repeatRules → series schedule
                  </span>
                  <span v-else-if="row.action === 'repaired_created_at'" class="text-blue-800 font-medium">
                    createdAt → series start
                  </span>
                  <span v-else-if="row.alreadyHadConfig" class="text-slate-500">
                    Config already existed
                  </span>
                  <span v-else class="text-slate-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div
        v-if="report.seriesMissingAfterApply?.length"
        class="bg-red-50 border border-red-200 rounded-lg p-5"
      >
        <h3 class="text-lg font-semibold text-red-900 mb-2">Missing after apply</h3>
        <ul class="list-disc list-inside font-mono text-xs text-red-800 space-y-1">
          <li v-for="id in report.seriesMissingAfterApply" :key="id">{{ id }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';
import migrationService from '@/services/migrationService';

export default {
  name: 'KssSeriesConfigMigrationTab',
  components: { Button },
  data() {
    return {
      loadingDryRun: false,
      loadingApply: false,
      dryRunReport: null,
      applyReport: null,
      errorMessage: null,
      rowFilter: 'all',
      filterOptions: [
        { value: 'all', label: 'All' },
        { value: 'create', label: 'Create / would create' },
        { value: 'manual', label: 'Needs manual pick' },
        { value: 'skipped', label: 'Skipped existing' },
        { value: 'failed', label: 'Failed' },
      ],
    };
  },
  computed: {
    report() {
      return this.applyReport || this.dryRunReport;
    },
    hasDryRunResults() {
      return this.dryRunReport != null;
    },
    hasApplyResults() {
      return this.applyReport != null;
    },
    wouldCreateOrCreatedCount() {
      if (!this.report) return 0;
      if (!this.report.dryRun) return this.report.seriesCreated ?? 0;
      return (this.report.rows || []).filter(
        (r) => r.action === 'would_create' || r.action === 'needs_manual_pick',
      ).length;
    },
    createdAtRepairedCount() {
      if (!this.report) return 0;
      if (!this.report.dryRun) return this.report.seriesCreatedAtRepaired ?? 0;
      return (this.report.rows || []).filter((r) => r.action === 'repaired_created_at').length;
    },
    scheduleRepairedCount() {
      if (!this.report) return 0;
      if (!this.report.dryRun) return this.report.seriesScheduleRepaired ?? 0;
      return (this.report.rows || []).filter((r) => r.action === 'repaired_schedule').length;
    },
    filteredRows() {
      const rows = this.report?.rows || [];
      if (this.rowFilter === 'create') {
        return rows.filter(
          (r) => r.action === 'would_create' || r.action === 'created' || r.action === 'needs_manual_pick',
        );
      }
      if (this.rowFilter === 'manual') {
        return rows.filter((r) => r.emptyRosterNeedsManualPick || r.action === 'needs_manual_pick');
      }
      if (this.rowFilter === 'skipped') {
        return rows.filter(
          (r) =>
            r.action === 'skipped_existing'
            || r.action === 'repaired_created_at'
            || r.action === 'repaired_schedule',
        );
      }
      if (this.rowFilter === 'failed') {
        return rows.filter((r) => r.action === 'failed');
      }
      return rows;
    },
  },
  methods: {
    formatSeriesStart(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return String(iso);
      return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' Z');
    },
    formatIdList(ids) {
      if (!ids?.length) return '';
      const shown = ids.slice(0, 3).map(String);
      const extra = ids.length - shown.length;
      return extra > 0 ? `${shown.join(', ')} (+${extra})` : shown.join(', ');
    },
    actionBadgeClass(action) {
      switch (action) {
        case 'failed':
          return 'bg-red-100 text-red-900';
        case 'created':
        case 'would_create':
          return 'bg-blue-100 text-blue-900';
        case 'repaired_created_at':
        case 'repaired_schedule':
          return 'bg-indigo-100 text-indigo-900';
        case 'needs_manual_pick':
          return 'bg-amber-100 text-amber-900';
        case 'skipped_existing':
          return 'bg-slate-100 text-slate-700';
        default:
          return 'bg-slate-100 text-slate-700';
      }
    },
    async runDryRun() {
      this.errorMessage = null;
      this.dryRunReport = null;
      this.applyReport = null;
      this.loadingDryRun = true;
      try {
        const res = await migrationService.runKssSeriesConfigMigrationDryRun();
        this.dryRunReport = res?.data?.data ?? res?.data ?? res;
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingDryRun = false;
      }
    },
    async runApply() {
      if (!this.hasDryRunResults) return;
      // eslint-disable-next-line no-alert
      const ok = window.confirm(
        'Apply KSS series config migration for the active tenant? This creates kss_series documents. Existing configs are skipped.',
      );
      if (!ok) return;

      this.errorMessage = null;
      this.applyReport = null;
      this.loadingApply = true;
      try {
        const res = await migrationService.runKssSeriesConfigMigration();
        this.applyReport = res?.data?.data ?? res?.data ?? res;
      } catch (e) {
        this.errorMessage = e?.response?.data?.message || e.message || String(e);
      } finally {
        this.loadingApply = false;
      }
    },
    clearResults() {
      this.dryRunReport = null;
      this.applyReport = null;
      this.errorMessage = null;
      this.rowFilter = 'all';
    },
  },
};
</script>
